import { FC } from "react";
import { Grid } from "@mui/material";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { DetailViewContainer } from "components/containers/DetailViewContainer";
import { DetailViewSection } from "components/containers/DetailViewSection";
import { DetailViewField } from "components/containers/DetailViewField";
import { DetailPageNavigation } from "components/containers/DetailPageNavigation";
import NotFoundPage from "components/layout/NotFoundPage";
import { Loading } from "components/utils/Loading";
import { SaleDetailsFragment, useSalesDetailQuery } from "lib/api";
import { formatPropertiesValue } from "utils/formatters";
import { Gallery } from "components/utils/Gallery";
import { Tags } from "components/utils/Tags";
import { Go3ToolboxRoutes } from "const/Go3ToolboxRoutes";

export const SalesDetailPage: FC = () => {
    const { utag } = useParams();
    const { t } = useTranslation();

    const { data, loading } = useSalesDetailQuery({
        skip: !utag,
        variables: { utag },
    });

    if (loading) return <Loading />;

    if (!utag || !data?.Propressale) return <NotFoundPage />;

    const { Propressale } = data;
    const images = Propressale.photoset.map((img) => ({ original: img?.url || "", thumbnail: img?.url || "" })) || [];
    const tags = Propressale.pslinkflag_entities?.map((pslink) => pslink?.flagname) || [];

    return (
        <>
            <DetailPageNavigation
                links={[
                    { href: `${Go3ToolboxRoutes.App.Sales.fullPath}/${utag}`, title: "Detail View" },
                    { href: `${Go3ToolboxRoutes.App.Sales.fullPath}/${utag}/activities`, title: t("translation:activities") },
                ]}
            />
            <Grid container spacing={2} alignItems="stretch">
                <Grid item xs={12} sm={12} md={12} lg={4}>
                    <DetailViewContainer<SaleDetailsFragment>
                        data={Propressale}
                        fields={[
                            "streetname1",
                            "streetnumber",
                            "propertyname",
                            "postcode",
                            "city",
                            "district_entity.districtname",
                            "area_entity.areaname",
                        ]}
                        label="translation:contacts.address"
                        formatter={formatPropertiesValue}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={4}>
                    <DetailViewContainer<SaleDetailsFragment>
                        data={Propressale}
                        fields={[
                            "listingowner_entity.title",
                            "listingowner_entity.company",
                            "listingowner_entity.firstname",
                            "listingowner_entity.surname",
                            "listingowner_entity.streetname1",
                            "listingowner_entity.streetnumber1",
                            "listingowner_entity.postalcode1",
                            "listingowner_entity.city1",
                            "listingowner_entity.mobileoffice",
                            "listingowner_entity.email1",
                        ]}
                        label="backend:listingowner"
                        formatter={formatPropertiesValue}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={4}>
                    <DetailViewContainer<SaleDetailsFragment>
                        data={Propressale}
                        fields={[
                            "askingprice",
                            "askingpricesurfsystres",
                            "propertytype",
                            "propertysubtypeValue",
                            "totalsurfaceres",
                            "surfacelivinarea",
                            "plotsurface",
                            "floordesc",
                            "numberlivingareas",
                            "numberbedrooms",
                            "totalnumberbathrooms",
                            "yearconstruction",
                            "plotprice",
                        ]}
                        translations={{
                            askingpricesurfsystres: "translation:properties.askingpricesurfsystres",
                            propertysubtypeValue: "propertysubtype",
                        }}
                        label="translation:key_facts"
                        formatter={formatPropertiesValue}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={4}>
                    <DetailViewContainer<SaleDetailsFragment>
                        data={Propressale}
                        fields={[
                            "propertystatus",
                            "lastaction",
                            "typeofmandate",
                            "mandateto",
                            "commissionpercent",
                            "commissionpercentowner",
                            "sign",
                            "keyonshop",
                            "keylabel",
                            "envsource",
                            "utag",
                        ]}
                        translations={{ lastaction: "translation:properties.lastaction", utag: "translation:utag" }}
                        label="translation:internal"
                        formatter={formatPropertiesValue}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={4}>
                    <DetailViewContainer<SaleDetailsFragment>
                        data={Propressale}
                        fields={["commentsbasicdata"]}
                        translations={{ commentsbasicdata: "commentsgeneral" }}
                        label="commentsbasicdata"
                        formatter={formatPropertiesValue}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={4}>
                    <DetailViewSection label={t("translation:other")}>
                        <DetailViewField label={t("backend:pslinkflags")}>
                            <Tags tags={tags} />
                        </DetailViewField>
                    </DetailViewSection>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={4}>
                    <DetailViewSection label={t("backend:photoset")}>
                        <Gallery images={images} />
                    </DetailViewSection>
                </Grid>
            </Grid>
        </>
    );
};
